/* ============================================================
   PRAWS · Bundle Fixed controller
   - Variant resolution + image swap on selector change
   - All items added in one Cart API call
   - Opens existing cart drawer (no redirect to /cart page)
   ============================================================ */

(function () {
  'use strict';

  /* ──────────────────────────────────────────────────────────────
     Move <section data-bf-bundle> INTO .product__info-container,
     right after the last Loox snippet block. Mirrors how the old app
     used to render (intercalated between Loox and the rest of the
     main-product blocks) so the visual hierarchy matches the live
     template the user already trusts.
     ────────────────────────────────────────────────────────────── */
  function relocateBundleSection() {
    const section = document.querySelector('section[data-bf-bundle]');
    if (!section || section.dataset.bfRelocated === 'true') return;

    const infoContainer =
      document.querySelector('.product__info-container') ||
      document.querySelector('.product__info-wrapper') ||
      document.querySelector('product-info .product__info') ||
      document.querySelector('product-info');
    if (!infoContainer) return;

    // Anchor: the LAST Loox block inside the info container. Fallback to price.
    let anchor = null;
    const looxBlocks = infoContainer.querySelectorAll(
      '.shopify-app-block[id*="loox_reviews_loox_snippets_widget"],' +
      ' .shopify-app-block[id*="loox_reviews_loox_rating"]'
    );
    looxBlocks.forEach((el) => { anchor = el; });
    if (!anchor) anchor = infoContainer.querySelector('.price');
    if (!anchor) anchor = infoContainer.firstElementChild;
    if (!anchor || !anchor.parentNode) return;

    anchor.parentNode.insertBefore(section, anchor.nextSibling);
    section.dataset.bfRelocated = 'true';
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', relocateBundleSection);
  } else {
    relocateBundleSection();
  }
  // Loox loads async, retry to make sure the anchor exists.
  setTimeout(relocateBundleSection, 400);
  setTimeout(relocateBundleSection, 1200);
  setTimeout(relocateBundleSection, 2500);

  // ────────── helpers ──────────
  const variantFeaturedImageUrl = (variant) => {
    if (!variant) return null;
    if (variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
    if (variant.image && variant.image.src) return variant.image.src;
    if (typeof variant.featured_image === 'string') return variant.featured_image;
    return null;
  };

  const ensureProtocol = (url) => {
    if (!url) return url;
    if (url.startsWith('//')) return window.location.protocol + url;
    return url;
  };

  // ────────── cart drawer integration (Slide Cart HQ - AMP) ──────────
  async function refreshAndOpenSlideCart() {
    // Prefer PAWS_CART_* (our custom slide cart) — AMP slidecart can re-bind
    // SLIDECART_* on its own init and steal control.
    const updateFn = window.PAWS_CART_UPDATE || window.SLIDECART_UPDATE;
    const openFn = window.PAWS_CART_OPEN || window.SLIDECART_OPEN;
    try {
      if (typeof updateFn === 'function') {
        const maybePromise = updateFn();
        if (maybePromise && typeof maybePromise.then === 'function') await maybePromise;
      }
    } catch (e) { console.warn('cart update failed:', e); }
    try {
      if (typeof openFn === 'function') {
        openFn();
        return true;
      }
    } catch (e) { console.warn('cart open failed:', e); }
    return false;
  }

  // ────────── main controller ──────────
  document.querySelectorAll('[data-bf-bundle]').forEach((root) => {
    const configEl = root.querySelector('[data-bf-config]');
    const config = configEl ? JSON.parse(configEl.textContent || '{}') : {};
    const ctaBtn = root.querySelector('[data-bf-checkout]');

    const resolveVariant = (card) => {
      const variantsAttr = card.dataset.componentVariants;
      const optionsAttr = card.dataset.componentOptions;
      if (!variantsAttr || !optionsAttr) return null;
      let variants, options;
      try {
        variants = JSON.parse(variantsAttr);
        options = JSON.parse(optionsAttr);
      } catch { return null; }
      if (variants.length === 1) return variants[0];

      const selectedByName = {};
      card.querySelectorAll('[data-bf-option-name]').forEach((input) => {
        if (input.checked) selectedByName[input.dataset.bfOptionName] = input.value;
      });
      const matched = variants.find((v) => {
        return v.options.every((optVal, idx) => {
          const optName = options[idx] && options[idx].name;
          return selectedByName[optName] === optVal;
        });
      });
      return matched || variants[0];
    };

    const updateCardImage = (card, variant) => {
      const imgEl = card.querySelector('.bf__card-img');
      if (!imgEl || !variant) return;
      const newUrl = variantFeaturedImageUrl(variant);
      if (!newUrl) return;
      const currentSrc = imgEl.currentSrc || imgEl.src || '';
      // Only swap if different (avoid flicker)
      if (currentSrc.split('?')[0] === ensureProtocol(newUrl).split('?')[0]) return;
      imgEl.removeAttribute('srcset');
      imgEl.src = ensureProtocol(newUrl);
    };

    // Disable inputs whose value would lead to an unavailable variant given
    // the current selection of OTHER options in this card. Prevents the user
    // from picking sold-out combinations (e.g. Arnés L Negro when that exact
    // combo is out of stock).
    const refreshUnavailableOptions = (card) => {
      const variantsAttr = card.dataset.componentVariants;
      const optionsAttr = card.dataset.componentOptions;
      if (!variantsAttr || !optionsAttr) return;
      let variants, options;
      try { variants = JSON.parse(variantsAttr); options = JSON.parse(optionsAttr); } catch { return; }

      // Current selection by option NAME
      const currentByName = {};
      card.querySelectorAll('[data-bf-option-name]').forEach((inp) => {
        if (inp.checked) currentByName[inp.dataset.bfOptionName] = inp.value;
      });

      card.querySelectorAll('[data-bf-option-name]').forEach((inp) => {
        const optName = inp.dataset.bfOptionName;
        // Build candidate selection swapping just this input's value
        const candidate = Object.assign({}, currentByName);
        candidate[optName] = inp.value;
        const match = variants.find((v) => {
          return v.options.every((optVal, idx) => {
            const name = options[idx] && options[idx].name;
            return candidate[name] === optVal;
          });
        });
        const available = !!(match && match.available);
        inp.disabled = !available;
        // Label is the next sibling element (label for swatch/pill)
        const label = inp.nextElementSibling;
        if (label) label.classList.toggle('paws-sold-out', !available);
      });
    };

    const updateCardStatus = (card) => {
      const variant = resolveVariant(card);
      const statusEl = card.querySelector('[data-bf-status]');

      card.querySelectorAll('[data-bf-option-position]').forEach((group) => {
        const checked = group.querySelector('input[type=radio]:checked');
        const lbl = group.querySelector('[data-bf-option-selected]');
        if (lbl && checked) lbl.textContent = checked.value;
      });

      refreshUnavailableOptions(card);

      if (!variant) return;
      updateCardImage(card, variant);

      if (statusEl) {
        if (variant.available) {
          statusEl.innerHTML = '<span class="bf__status-ok">✓ Disponible</span>';
        } else {
          statusEl.innerHTML = '<span class="bf__status-soldout">Agotado en esta variante</span>';
        }
      }
    };

    // ── Format helper for CLP (cents → "$X.XXX") ──
    const formatCLP = (cents) => '$' + Math.round((Number(cents) || 0) / 100).toLocaleString('es-CL').replace(/,/g, '.');

    // ── Recalculate BOTH prices dynamically:
    //   - "original" (strike-through) = sum of selected variant retail prices
    //   - "discounted" = original × (1 - discount_pct/100), matching the
    //     Automatic Discount that applies at cart/checkout.
    // Cards deactivated by the type selector (correa clásica vs manos libres)
    // don't count for price, availability, or cart add.
    const isActiveCard = (card) => card.dataset.bfInactive !== 'true';

    const refreshButtonPrices = () => {
      const originalEl = root.querySelector('[data-bf-original-price]');
      const discountedEl = root.querySelector('[data-bf-discounted-price]');
      let originalSum = 0;
      root.querySelectorAll('[data-bf-component]').forEach((card) => {
        if (!isActiveCard(card)) return;
        const v = resolveVariant(card);
        if (v) originalSum += Number(v.price) || 0;
      });
      const pct = Number(ctaBtn?.dataset.bfDiscountPercent || 0);
      const discountedSum = Math.round(originalSum * (100 - pct) / 100);
      if (originalEl) originalEl.textContent = formatCLP(originalSum);
      if (discountedEl) discountedEl.textContent = formatCLP(discountedSum);
    };

    const allComponentsAvailable = () => {
      let ok = true;
      root.querySelectorAll('[data-bf-component]').forEach((card) => {
        if (!isActiveCard(card)) return;
        const variant = resolveVariant(card);
        if (!variant || !variant.available) ok = false;
      });
      return ok;
    };

    // ── Type selector (e.g. Correa Clásica vs Manos Libres) ──
    const setActiveChoice = (group, value) => {
      root.querySelectorAll('[data-bf-component][data-bf-choice-group="' + group + '"]').forEach((card) => {
        const active = card.dataset.bfChoiceValue === value;
        card.dataset.bfInactive = active ? 'false' : 'true';
        if (active) card.removeAttribute('hidden');
        else card.setAttribute('hidden', 'hidden');
        card.querySelectorAll('[data-bf-type-choice]').forEach((inp) => {
          inp.checked = inp.value === value;
        });
        if (active) updateCardStatus(card);
      });
      refreshCTA();
      refreshButtonPrices();
    };

    const refreshCTA = () => {
      if (ctaBtn) ctaBtn.disabled = !allComponentsAvailable();
    };

    const addBundleToCart = async () => {
      if (!allComponentsAvailable()) return;
      ctaBtn.disabled = true;
      const originalHTML = ctaBtn.innerHTML;
      ctaBtn.innerHTML = '<span>Agregando...</span>';

      let succeeded = false;
      try {
        // ── 1) Build items (skip cards deactivated by the type selector)
        const items = [];
        let position = 1;
        root.querySelectorAll('[data-bf-component]').forEach(function (card) {
          if (!isActiveCard(card)) return;
          const v = resolveVariant(card);
          if (v) {
            items.push({
              id: v.id,
              quantity: 1,
              properties: { '_bundle': config.bundle_handle, '_bundle_position': position },
            });
            position += 1;
          }
        });

        // ── 2) Add to cart
        const addRes = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items }),
        });
        if (!addRes.ok) throw new Error('Cart add failed: ' + addRes.status);
        const addData = await addRes.json();
        console.log('[paws-bundle] cart/add done, items:', (addData && addData.items && addData.items.length) || 0);

        // ── 3) Apply discount code (separate statement, no comma operator)
        const discountCode = config && config.discount_code;
        console.log('[paws-bundle] discount_code from config:', discountCode);
        if (discountCode) {
          console.log('[paws-bundle] applying discount via /discount/' + discountCode);
          try {
            const discountRes = await fetch(
              '/discount/' + encodeURIComponent(discountCode) + '?_t=' + Date.now(),
              { method: 'GET', credentials: 'include', redirect: 'follow' }
            );
            await discountRes.text();
            console.log('[paws-bundle] /discount fetch returned status', discountRes.status);
          } catch (e) {
            console.warn('[paws-bundle] discount apply failed:', e);
          }
          // Wait so the discount_code cookie persists before next /cart.js
          await new Promise(function (r) { setTimeout(r, 400); });
        }

        succeeded = true;
      } catch (err) {
        console.error('[paws-bundle] error:', err);
        alert('Hubo un problema agregando el bundle. Intenta de nuevo.');
      } finally {
        ctaBtn.disabled = false;
        ctaBtn.innerHTML = originalHTML;
      }

      if (succeeded) {
        const opened = await refreshAndOpenSlideCart();
        if (!opened) {
          window.location.href = '/cart';
        }
      }
    };

    // Wire up
    root.querySelectorAll('[data-bf-component]').forEach((card) => {
      card.querySelectorAll('input[type=radio]').forEach((input) => {
        if (input.hasAttribute('data-bf-type-choice')) {
          input.addEventListener('change', () => {
            if (input.checked) setActiveChoice(input.dataset.bfGroup, input.value);
          });
          return;
        }
        input.addEventListener('change', () => {
          updateCardStatus(card);
          refreshCTA();
          refreshButtonPrices();
        });
      });
      updateCardStatus(card);
    });

    if (ctaBtn) ctaBtn.addEventListener('click', addBundleToCart);
    refreshCTA();
    refreshButtonPrices();
  });
})();
